import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import polarisLogo from "../images/Polaris Logo.svg";
import {
    ensureLoginScreenHeroReady,
    prefetchLoginScreenHero,
} from "../services/loginScreenService";
import "../styles/landing-page.css";

interface PublicHeaderProps {
    logoLinkTo?: string;
}

const SOLUTIONS_LINKS = [
    { label: "Schools", to: "/schools" },
    { label: "Organizations", to: "/organizations" },
    { label: "Families", to: "/families" },
];

export default function PublicHeader({ logoLinkTo = "/" }: PublicHeaderProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [solutionsOpen, setSolutionsOpen] = useState(false);
    const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
    const headerRef = useRef<HTMLElement>(null);
    const navigate = useNavigate();

    const closeMenu = () => {
        setMenuOpen(false);
        setMobileSolutionsOpen(false);
    };

    useEffect(() => {
        prefetchLoginScreenHero();
    }, []);

    useEffect(() => {
        if (!menuOpen && !solutionsOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
                closeMenu();
                setSolutionsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpen, solutionsOpen]);

    const handleNavLink = (to: string) => {
        closeMenu();
        navigate(to);
    };

    const navigateToLogin = async () => {
        closeMenu();
        await ensureLoginScreenHeroReady();
        navigate("/login");
    };

    return (
        <header className="landing-header" ref={headerRef}>
            <div className="landing-header-inner">
                <Link to={logoLinkTo} onClick={closeMenu}>
                    <img src={polarisLogo} alt="Polaris Athlete" className="landing-logo" />
                </Link>

                <nav className="landing-header-nav">
                    <Link to="/about" className="landing-header-link">About</Link>
                    <Link to="/our-team" className="landing-header-link">Our Team</Link>

                    <div
                        className="landing-solutions"
                        onMouseEnter={() => setSolutionsOpen(true)}
                        onMouseLeave={() => setSolutionsOpen(false)}
                    >
                        <button
                            type="button"
                            className="landing-header-link landing-solutions-toggle"
                            aria-haspopup="true"
                            aria-expanded={solutionsOpen}
                            onClick={() => setSolutionsOpen(prev => !prev)}
                        >
                            Solutions
                            <span className={`landing-solutions-chevron${solutionsOpen ? " open" : ""}`} aria-hidden="true" />
                        </button>
                        {solutionsOpen && (
                            <div className="landing-solutions-menu" role="menu">
                                {SOLUTIONS_LINKS.map(({ label, to }) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        className="landing-solutions-menu-link"
                                        role="menuitem"
                                        onClick={() => setSolutionsOpen(false)}
                                    >
                                        {label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    <Link to="/signup" className="landing-header-link">Plans</Link>
                    <Link to="/blogs" className="landing-header-link">Blog</Link>

                    <Link to="/contact" className="landing-getdemo-btn">Get a Demo</Link>
                    <Link
                        to="/login"
                        className="landing-signup-btn"
                        onMouseEnter={prefetchLoginScreenHero}
                        onFocus={prefetchLoginScreenHero}
                        onClick={(event) => {
                            event.preventDefault();
                            void navigateToLogin();
                        }}
                    >
                        Sign In
                    </Link>

                    <button
                        className="landing-hamburger"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(prev => !prev)}
                    >
                        <span className={`landing-hamburger-bar${menuOpen ? " open" : ""}`} />
                        <span className={`landing-hamburger-bar${menuOpen ? " open" : ""}`} />
                        <span className={`landing-hamburger-bar${menuOpen ? " open" : ""}`} />
                    </button>
                </nav>
            </div>

            {menuOpen && (
                <div className="landing-mobile-nav" role="menu">
                    <button className="landing-mobile-link" onClick={() => handleNavLink("/about")} role="menuitem">About</button>
                    <button className="landing-mobile-link" onClick={() => handleNavLink("/our-team")} role="menuitem">Our Team</button>
                    <button
                        className="landing-mobile-link landing-mobile-solutions-toggle"
                        aria-expanded={mobileSolutionsOpen}
                        onClick={() => setMobileSolutionsOpen(prev => !prev)}
                        role="menuitem"
                    >
                        Solutions
                        <span className={`landing-solutions-chevron${mobileSolutionsOpen ? " open" : ""}`} aria-hidden="true" />
                    </button>
                    {mobileSolutionsOpen && (
                        <div className="landing-mobile-subnav">
                            {SOLUTIONS_LINKS.map(({ label, to }) => (
                                <button
                                    key={to}
                                    className="landing-mobile-link landing-mobile-sublink"
                                    onClick={() => handleNavLink(to)}
                                    role="menuitem"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <button className="landing-mobile-link" onClick={() => handleNavLink("/signup")} role="menuitem">Plans</button>
                    <button className="landing-mobile-link" onClick={() => handleNavLink("/blogs")} role="menuitem">Blog</button>
                    <button className="landing-mobile-getdemo" onClick={() => handleNavLink("/contact")} role="menuitem">Get a Demo</button>
                    <button
                        className="landing-mobile-signup"
                        onTouchStart={prefetchLoginScreenHero}
                        onClick={() => {
                            void navigateToLogin();
                        }}
                        role="menuitem"
                    >
                        Sign In
                    </button>
                </div>
            )}
        </header>
    );
}
